import React, { FC, useCallback, useMemo, useState } from 'react';

import { Box, Center, Spinner, useDisclosure } from '@chakra-ui/react';

import { renderTimeStackedCell, StackedCell } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import { ListIcon } from 'components/Icons';

import {
  formatSoftwareCount,
  formatSoftwareEOL,
  getSoftwareEcosystemIcon,
  getSoftwareEcosystemLabel,
  getSoftwareTechnologyIcon,
  OPERATING_SYSTEM_CATEGORY,
  SoftwareInventoryEntityRow,
} from '../utils';

import { SoftwareInventoryInstallationsDrawer } from './SoftwareInventoryInstallationsDrawer';

interface Props {
  category: string;
  rows: SoftwareInventoryEntityRow[];
  isLoading: boolean;
  isError: boolean;
  isLoadingMore?: boolean;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
}

export const SoftwareInventoryEntityTable: FC<Props> = ({
  category,
  rows,
  isLoading,
  isError,
  isLoadingMore = false,
  onScroll,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPackage, setSelectedPackage] = useState<
    SoftwareInventoryEntityRow | undefined
  >();

  const handleShowEntities = useCallback(
    (row: SoftwareInventoryEntityRow) => {
      setSelectedPackage(row);
      onOpen();
    },
    [onOpen],
  );

  const handleClose = useCallback(() => {
    onClose();
    setSelectedPackage(undefined);
  }, [onClose]);

  const isOperatingSystem = category === OPERATING_SYSTEM_CATEGORY;
  const columns = useMemo(
    () => [
      {
        header: 'Name',
        align: 'left',
        styles: { width: '30%', cell: { width: '30%' } },
        render: ({ row }) => (
          // Technologies show the product and the packages behind it
          // (MySQL / mysql-server-8.0) rather than the package manager.
          <StackedCell
            upper={row.technologyName ?? row.displayName ?? row.name}
            lower={
              row.technologyKey
                ? row.packageNames?.join(', ') || row.name
                : getSoftwareEcosystemLabel(row.ecosystem)
            }
            showUpperTooltip={false}
            showLowerTooltip={!!row.technologyKey}
            iconProps={{
              resourceType: row.technologyKey
                ? getSoftwareTechnologyIcon(row.technologyKey)
                : getSoftwareEcosystemIcon(row?.ecosystem),
            }}
          />
        ),
      },
      // Cloud is not provided by the packages API.
      {
        header: 'Deployed In',
        styles: { width: '25%', cell: { width: '25%' } },
        render: ({ row }) => (
          <StackedCell
            upper={row.deployedIn.upper}
            lower={row.deployedIn.lower}
            showUpperTooltip={false}
            showLowerTooltip={false}
          />
        ),
      },
      {
        header: 'Hosts',
        styles: { width: '15%', cell: { width: '15%' } },
        render: ({ row }) => formatSoftwareCount(row.hosts),
      },
      // A release row is a single version, so OS releases show EOL instead.
      isOperatingSystem
        ? {
            header: 'End of Life',
            styles: { width: '13%', cell: { width: '13%' } },
            render: ({ row }) => {
              const eol = formatSoftwareEOL(row.eolDate);
              return (
                <StackedCell
                  upper={eol.date}
                  lower={eol.status}
                  showUpperTooltip={false}
                  showLowerTooltip={false}
                  styles={
                    eol.isPast ? { lower: { color: 'critical' } } : undefined
                  }
                />
              );
            },
          }
        : {
            header: 'Versions',
            styles: { width: '13%', cell: { width: '13%' } },
            render: ({ row }) => formatSoftwareCount(row.versions),
          },
      {
        header: 'Last Seen',
        styles: { width: '11%', cell: { width: '11%' } },
        render: ({ row }) =>
          renderTimeStackedCell({ value: row.lastObservedAt }),
      },
      {
        header: 'Actions',
        styles: { width: '6%', cell: { width: '6%' } },
        render: ({ row }) => (
          <Center>
            <ActionButton
              label="Show entities"
              icon={<ListIcon />}
              styles={{ color: 'primary' }}
              // Open on click only: onMouseDown also fired, opening the drawer twice per click.
              onClick={() => handleShowEntities(row)}
            />
          </Center>
        ),
      },
    ],
    [handleShowEntities, isOperatingSystem],
  );

  return (
    <Box h="full" position="relative" onScrollCapture={onScroll}>
      <Table
        columns={columns}
        data={rows}
        isLoading={isLoading && !isLoadingMore}
        isError={isError}
        noDataMessage="No software entities available"
      />
      {isLoadingMore && (
        <Center position="absolute" bottom={2} left={0} right={0}>
          <Spinner size="sm" />
        </Center>
      )}
      {selectedPackage && (
        <SoftwareInventoryInstallationsDrawer
          category={category}
          packageRow={selectedPackage}
          isOpen={isOpen}
          onClose={handleClose}
        />
      )}
    </Box>
  );
};
